"use client";
import React, { useState } from "react";
import { PortableText } from "@portabletext/react";
import styles from "./faqSection.module.css";

export interface FaqItem {
  id: string;
  question: string;
  answer: any; // Portable text array
}

export interface FaqSectionProps {
  title?: string;
  subtitle?: string;
  description?: string;
  displayStyle?: "accordion" | "grid" | "list";
  faqs?: FaqItem[];
}

const fallbackFaqs: FaqItem[] = [
  {
    id: "faq-1",
    question: "What makes these solutions different from other consulting approaches?",
    answer: [
      {
        _type: "block",
        _key: "answer-1",
        style: "normal",
        children: [
          {
            _type: "span",
            _key: "span-1",
            text: "These solutions focus on practical implementation rather than theory. Every framework is battle-tested across multiple teams and environments. I prioritize simplicity and repeatability over complex methodologies that create overhead without delivering results.",
            marks: [],
          },
        ],
        markDefs: [],
      },
    ],
  },
  {
    id: "faq-2",
    question: "How long does it take to implement these solutions?",
    answer: [
      {
        _type: "block",
        _key: "answer-2",
        style: "normal",
        children: [
          {
            _type: "span",
            _key: "span-2",
            text: "Implementation timelines vary based on team size and organizational complexity. Most teams see measurable improvements within 4-6 weeks. Full adoption typically takes 3-6 months as processes become habits and teams build confidence in the new workflows.",
            marks: [],
          },
        ],
        markDefs: [],
      },
    ],
  },
  {
    id: "faq-3",
    question: "Can these solutions work for small teams or startups?",
    answer: [
      {
        _type: "block",
        _key: "answer-3",
        style: "normal",
        children: [
          {
            _type: "span",
            _key: "span-3",
            text: "Absolutely. These frameworks scale both up and down. Small teams benefit from lightweight processes that prevent chaos as they grow. Startups gain structure without bureaucracy. The solutions adapt to your current stage while providing a path for scaling as your team expands.",
            marks: [],
          },
        ],
        markDefs: [],
      },
    ],
  },
  {
    id: "faq-4",
    question: "Do I need to adopt all solutions at once?",
    answer: [
      {
        _type: "block",
        _key: "answer-4",
        style: "normal",
        children: [
          {
            _type: "span",
            _key: "span-4",
            text: "No. These solutions work independently or together. Most organizations start with one or two areas that address their biggest pain points. Common starting points include DevOps for deployment confidence or Agile/SCRUM for team coordination. You can add additional solutions as needs emerge.",
            marks: [],
          },
        ],
        markDefs: [],
      },
    ],
  },
];

const FaqItem: React.FC<{
  faq: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ faq, isOpen, onToggle }) => {
  return (
    <article
      className={`${styles.faqItem} ${isOpen ? styles.faqItemOpen : ""}`}
    >
      <button
        onClick={onToggle}
        className={styles.faqItemButton}
        aria-expanded={isOpen}
        aria-label={isOpen ? `Collapse answer for: ${faq.question}` : `Expand answer for: ${faq.question}`}
        type="button"
      >
        <div className={styles.faqItemContent}>
          <span className={styles.faqIcon} aria-hidden="true">
            {isOpen ? "−" : "+"}
          </span>
          <h3 className={styles.faqQuestion}>{faq.question}</h3>
        </div>
      </button>
      {isOpen && (
        <div className={styles.faqAnswer} onClick={(e) => e.stopPropagation()}>
          {Array.isArray(faq.answer) ? (
            <PortableText value={faq.answer} />
          ) : (
            <p>{faq.answer}</p>
          )}
        </div>
      )}
    </article>
  );
};

const FaqSection: React.FC<FaqSectionProps> = ({
  title = "Frequently Asked Questions",
  subtitle,
  description,
  displayStyle = "accordion",
  faqs = fallbackFaqs,
}) => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Filter out FAQs without questions
  const validFaqs = faqs.filter((faq) => faq.question && faq.answer);

  if (validFaqs.length === 0) {
    return null;
  }

  return (
    <section
      className={styles.faqSection}
      aria-label="Frequently asked questions"
    >
      <div className={styles.faqContainer}>
        <header className={styles.faqHeader}>
          {subtitle && (
            <p className={styles.faqSubtitle} role="doc-subtitle">
              {subtitle}
            </p>
          )}
          <h2 className={styles.faqTitle}>{title}</h2>
          {description && (
            <p className={styles.faqDescription}>{description}</p>
          )}
        </header>

        <div
          className={`${styles.faqList} ${
            displayStyle === "grid" ? styles.faqListGrid : ""
          } ${displayStyle === "list" ? styles.faqListList : ""}`}
        >
          {validFaqs.map((faq) => (
            <FaqItem
              key={faq.id}
              faq={faq}
              isOpen={openItems.has(faq.id)}
              onToggle={() => toggleItem(faq.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;

